import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Calendar, Sparkles } from "lucide-react";
import { TOUR_DATES, PHOTOS } from "../mock";
import { toast } from "sonner";

export default function Tournee() {
  const [hoverId, setHoverId] = useState(null);

  const handleReminder = (city) => {
    toast.success(`Rappel ajouté — ${city}`, {
      description: "Nous vous tiendrons informé dès l'ouverture de la billetterie.",
    });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white">Accueil</Link>
        <span>/</span>
        <span className="text-white">Tournée 2026</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            03 · Tournée France · Juin 2026
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            En tournée<br />
            <span className="text-blue-600">partout en France</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="accent-line-blue" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">10 dates · 9 villes</span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Du 4 au 21 juin 2026, Vince Rodi part à la rencontre de son public dans 9 villes de France, pour s'achever en beauté à la Fête de la Musique à Paris.
          </p>
          <div className="mt-4 flex gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Dates</div>
              <div className="font-display text-3xl">{TOUR_DATES.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Villes</div>
              <div className="font-display text-3xl">9</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Mois</div>
              <div className="font-display text-3xl">Juin</div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero image — affiche officielle complète */}
      <div className="flex justify-center bg-neutral-950 mb-14 reveal-up">
        <img
          src={PHOTOS.tour}
          alt="Affiche officielle tournée Vince Rodi 2026"
          className="w-auto max-w-full max-h-[85vh] object-contain"
        />
      </div>

      {/* Date list */}
      <ul className="divide-y divide-white/10 border-y border-white/10">
        {TOUR_DATES.map((d, i) => (
          <li
            key={d.id}
            id={`date-${d.id}`}
            onMouseEnter={() => setHoverId(d.id)}
            onMouseLeave={() => setHoverId(null)}
            className={`group relative grid grid-cols-12 gap-3 md:gap-6 items-center py-5 md:py-7 transition-all duration-500 scroll-mt-28 ${
              hoverId === d.id ? "pl-3 md:pl-5" : ""
            }`}
          >
            <div className="col-span-2 md:col-span-1">
              <span className="text-xs text-neutral-500 tabular-nums">0{i + 1 < 10 ? i + 1 : i + 1}</span>
            </div>
            <div className="col-span-3 md:col-span-2 flex items-baseline gap-2">
              <span className="font-display text-4xl md:text-5xl">{d.day}</span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{d.month}</span>
            </div>
            <div className="col-span-7 md:col-span-5">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight">
                  {d.city}
                </h3>
                {d.highlight && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-red-600 text-white px-2 py-0.5">
                    <Sparkles size={10} /> Fête de la Musique
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1">
                <MapPin size={12} />
                <span>{d.region}</span>
                <span className="text-neutral-700">•</span>
                <span>{d.venue}</span>
              </div>
            </div>
            <div className="hidden md:flex col-span-2 items-center">
              <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">2026</span>
            </div>
            <div className="col-span-12 md:col-span-2 flex md:justify-end mt-2 md:mt-0">
              <button
                onClick={() => handleReminder(d.city)}
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white hover:bg-white hover:text-black transition-all px-4 py-2 text-[10px] uppercase tracking-[0.2em]"
              >
                <Calendar size={12} /> Me prévenir
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-16 border border-white/15 p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-3">Pro & organisateurs</div>
          <h3 className="font-display text-4xl md:text-5xl uppercase leading-[0.95]">
            Vous organisez<br /> un événement ?
          </h3>
          <p className="text-neutral-400 text-sm mt-4 max-w-md">
            Festival, soirée privée, programmation : contactez directement le label pour toute demande de booking.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <a
            href="mailto:vince.rodimusic@gmail.com"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition px-5 py-3 text-xs uppercase tracking-[0.2em]"
          >
            Contacter pour booking <ArrowUpRight size={14} />
          </a>
          <Link to="/contact" className="text-sm text-neutral-400 hover:text-white">
            Voir tous les contacts →
          </Link>
        </div>
      </div>
    </div>
  );
}
